import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

import { config } from '@/core/config/env'
import { authEvents } from '@/core/lib/auth-events'
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from '@/core/lib/token-store'
import { notify } from '@/core/lib/ui-bus'

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

const NOTIFY_DEDUPE_MS = 2500
const lastNotifiedAt: Record<string, number> = {}

const notifyOnce = (key: string, fn: () => void) => {
  const now = Date.now()
  if (now - (lastNotifiedAt[key] ?? 0) < NOTIFY_DEDUPE_MS) return
  lastNotifiedAt[key] = now
  fn()
}

// withCredentials: refresh token httpOnly cookie'sini her istekte gönder/al.
export const api = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true,
})

// Refresh çağrısı için interceptor'sız ayrı instance (sonsuz özyinelemeyi önler).
const refreshClient = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true,
})

// 401 mutex: eşzamanlı 401'ler tek bir refresh sözünü paylaşır (single-flight).
let refreshPromise: Promise<string | null> | null = null

function runRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    // Refresh token cookie'de; gövde yok. Cookie geçerliyse yeni access token döner,
    // yoksa/expire ise backend 401 verir → null (oturum yok).
    refreshPromise = refreshClient
      .get<{ data: { access_token: string } }>('/auth/access-token')
      .then((res) => res.data.data.access_token)
      .catch(() => null)
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

api.interceptors.request.use((cfg) => {
  const token = getAccessToken()
  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`
  }
  return cfg
})

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableConfig | undefined
    const status = error.response?.status

    if (status === 429) {
      notifyOnce('429', () =>
        notify.error({
          message: 'Çok fazla istek',
          description:
            'Çok fazla istek gönderildi. Lütfen biraz sonra tekrar deneyin.',
          placement: 'topRight',
          duration: 3,
        })
      )
      return Promise.reject(error)
    }

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true
      // Bellekte access token varsa kullanıcı aktif oturumdaydı; refresh başarısız olursa
      // bu gerçek "oturum bitti" anıdır. Yoksa (taze yükleme / hiç girilmemiş) sessiz kal:
      // route guard yönlendirir, gereksiz "oturum kapandı" uyarısı çıkmaz.
      const hadSession = getAccessToken() !== null
      const newToken = await runRefresh()
      if (newToken) {
        setAccessToken(newToken)
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      }
      // Refresh başarısız: yalnız access token'ı temizle (refresh cookie httpOnly,
      // transient backend hatasında dokunulmaz → sonraki yenilemede kendini kurtarır).
      clearAccessToken()
      if (hadSession) {
        authEvents.emit('signed-out')
      }
      return Promise.reject(error)
    }

    if (status === 403) {
      authEvents.emit('forbidden')
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)
